import { WorksheetBatchUpdateAllTestsTypeId, getWorksheetBatchUpdateDefinition } from 'Components/WorksheetBatchUpdate';
import { IDataSyncModel } from 'Data/ViewModels/IDataSyncModel';
import { hasEmptyValue, hasValue } from 'Lib/Any';
import { joinPaths } from 'Lib/Object/Path/shared';
import * as ioPath from '../../../Lib/Object/Path/Immutable';
import { WorksheetBatchUpdateQuery, WorksheetBatchUpdateQueryParams } from './types';

/**
 * Assembles a query for fetching worksheets for the batch update by merging parameters from multiple sources
 *
 * @param {string} typeId - The type id (a.k.a. QestId) of the worksheet being fetched
 * @param {WorksheetBatchUpdateQueryParams} uriParams - Params that were passed in via the Web App uri
 * @param {{[key: string]: any}} enteredParams - Params that were entered by the user. These may need to be transformed into uri compatible strings.
 */
export const assembleQuery = (
  typeId: string,
  uriParams: WorksheetBatchUpdateQueryParams,
  enteredParams: { [key: string]: any }
): WorksheetBatchUpdateQuery => {
  const defn = getWorksheetBatchUpdateDefinition(typeId);

  if (hasEmptyValue(defn)) return { params: {}, isValid: false };

  // Assemble params for query in priority order
  // Default params < Uri params < Entered params < Fixed params
  const params: WorksheetBatchUpdateQueryParams = {
    ...defn.query.defaultParams,
    ...Object.fromEntries(Object.entries(uriParams).filter(([k, _]) => k in defn.queryFields)),
    ...Object.fromEntries(
      Object.entries(enteredParams).map(([k, v]) => [
        k,
        hasValue(defn.queryFields[k]?.valueToParam) ? defn.queryFields[k].valueToParam!(v) : v,
      ])
    ),
    ...(typeId !== WorksheetBatchUpdateAllTestsTypeId && { TypeId: typeId }),
    // We don't support pagination of results so provide a fixed "reasonable" limit to get all results (unless overridden)
    Limit: '999',
  };

  return {
    params,
    isValid: !defn.query.requiredParams.some((p) => !hasValue(params[p])),
  };
};

/**
 * Remaps params to api request params as defined in WorksheetBatchUpdateDefinition
 *
 * In general this should not be required as params should be given the same names as the API endpoint params.
 * We only use this when we need to ensure consistency across test types.
 */
export const remapQueryParamsForApiRequest = (typeId: string, params: WorksheetBatchUpdateQueryParams) => {
  const paramNameRemap = getWorksheetBatchUpdateDefinition(typeId)?.query?.requestParamRenameMap;
  if (hasEmptyValue(paramNameRemap)) return params;
  return Object.fromEntries(Object.entries(params).map(([k, v]) => [paramNameRemap![k] ?? k, v]));
};

/**
 * Returns true if the field identified by importSourceFieldKey
 * is an import source where the imported values mismatch on worksheetModel
 */
export const hasImportMismatch = (typeId: string, worksheetModel: IDataSyncModel, importSourceFieldKey: string) => {
  const importSourceField = getWorksheetBatchUpdateDefinition(typeId)?.worksheetFields[importSourceFieldKey];
  if (hasEmptyValue(importSourceField) || !importSourceField?.isImportSource) return false;

  const mismatchFlagFields = [
    joinPaths(importSourceFieldKey, 'Mismatch'),
    importSourceField.equipmentDetails?.calibrationImportMismatchField,
    importSourceField.equipmentDetails?.propertiesImportMismatchField,
  ].filter(hasValue);

  return mismatchFlagFields.some((mp) => ioPath.get(worksheetModel, mp));
};
